// src/components/features/Vehicle/MobileVehicleCard/MobileVehicleCard.tsx
//
// Mobile-only vehicle card for the single-vehicle dashboard view.
//
// Displays:
// 1. Gradient card with odometer + period selector + stats (spent/fuel/economy)
// 2. Quick action buttons below the card
// 3. Optional scan receipt button
//
// Tabs: current month | previous month | rolling avg | TCO (lifetime cost)
//
// This component does NOT show the vehicle header (name/image) because
// the MobileVehicleSwitcher above the card already displays that info.

import React, { useState, useMemo } from "react";
import {
  Box,
  Group,
  Stack,
  Text,
  SegmentedControl,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { toCamelCase } from "@/utils/misc";
import { VehicleType } from "@/utils/carHelpers";

import {
  DashboardItem,
  CarStats,
  StatsMonetary,
  StatsUnits,
  DashboardAverages,
  CostOfOwnership,
} from "@/utils/api/DashboardService";
import { ReceiptScanResult } from "@/utils/api/ScannerService";

import { DetectLocationFn } from "@/hooks/useScanner";
import { AppIcon } from "@/components/generic/AppIcon";
import {
  VehicleCardQuickActions,
  QuickActionType,
} from "../VehicleCardQuickActions";


// ============================================
// Types
// ============================================

export interface MobileVehicleCardProps {
  /** Dashboard item with all pre-aggregated stats */
  item: DashboardItem;

  /** Car ID for quick actions */
  carId: string;

  /** Default currency code */
  currency?: string;

  /** Vehicle type for efficiency status */
  vehicleType?: VehicleType;

  /** Handler for quick action click */
  onQuickAction?: (action: QuickActionType, carId: string) => void;

  /** Whether to show the scan receipt button */
  showScanReceipt?: boolean;

  /** Handler for scan receipt completion */
  onScanComplete?: (result: ReceiptScanResult, carId?: string | null) => void;

  /** Optional location detection function for scan pipeline */
  onDetectLocation?: DetectLocationFn;

  /** Custom class name */
  className?: string;
}

/** Which period tab is selected */
type StatsPeriod = "current" | "previous" | "average" | "tco";

// ============================================
// Unit Translation Helpers
// ============================================

const getDistanceUnitLabel = (
  unit: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): string => {
  const key = `common:units.distance.${toCamelCase(unit)}Short`;
  const translated = t(key, { defaultValue: "" });
  return translated || unit;
};

const getVolumeUnitLabel = (
  unit: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): string => {
  const key = `common:units.volume.${toCamelCase(unit)}Short`;
  const translated = t(key, { defaultValue: "" });
  return translated || unit;
};

const getConsumptionUnitLabel = (
  unit: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): string => {
  const key = `common:units.consumption.${toCamelCase(unit)}Short`;
  const translated = t(key, { defaultValue: "" });
  return translated || unit;
};

// ============================================
// Utility Functions
// ============================================

const getTotalCost = (monetary: StatsMonetary): number => {
  return (
    Number(monetary.refuelsCost || 0) +
    Number(monetary.refuelsTaxes || 0) +
    Number(monetary.expensesCost || 0) +
    Number(monetary.expensesFees || 0) +
    Number(monetary.expensesTaxes || 0)
  );
};

const getAdaptiveStatFontSize = (value: string): string => {
  const len = value.length;
  if (len <= 5) return "1.25rem";
  if (len <= 7) return "1.1rem";
  if (len <= 9) return "0.95rem";
  return "0.85rem";
};

const getAdaptiveOdometerFontSize = (value: string): string => {
  const len = value.length;
  if (len <= 5) return "2rem";
  if (len <= 7) return "1.75rem";
  if (len <= 9) return "1.5rem";
  return "1.3rem";
};

const formatAmount = (amount: number): string => {
  return amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// ============================================
// Period Display Values
// ============================================

interface CurrencyCost {
  currency: string;
  totalCost: number;
}

interface PeriodDisplayValues {
  costs: CurrencyCost[];
  col2Value: string;
  col2UnitLabel: string;
  col3Value: string;
  col3UnitLabel: string;
}

function getMonthDisplayValues(
  stats: CarStats,
  monetary: StatsMonetary[],
  fallbackCurrency: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): PeriodDisplayValues {
  const costs: CurrencyCost[] = monetary.map((m) => ({
    currency: m.currency || fallbackCurrency,
    totalCost: getTotalCost(m),
  }));

  const units: StatsUnits | undefined = stats.inUserUnits || stats.inCarUnits;
  const volumeUnit = units?.volumeUnit || "l";
  const consumptionUnit = units?.consumptionUnit || "l100km";
  const refuelsVolume = units?.refuelsVolume ?? 0;
  const consumption = units?.consumption ?? null;

  return {
    costs,
    col2Value: refuelsVolume > 0 ? refuelsVolume.toFixed(1) : "0",
    col2UnitLabel: getVolumeUnitLabel(volumeUnit, t),
    col3Value: consumption != null && consumption > 0 ? consumption.toFixed(1) : "—",
    col3UnitLabel:
      consumption != null && consumption > 0
        ? getConsumptionUnitLabel(consumptionUnit, t)
        : "",
  };
}

function getAverageDisplayValues(
  item: DashboardItem,
  fallbackCurrency: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): PeriodDisplayValues {
  const avg = item.averages;
  const currency = avg?.currency || fallbackCurrency;
  const monthlyExpense = avg?.monthlyExpense ?? 0;

  const units =
    item.currentMonthStats?.inUserUnits || item.currentMonthStats?.inCarUnits;
  const volumeUnit = units?.volumeUnit || "l";
  const consumptionUnit = units?.consumptionUnit || "l100km";
  const monthlyVolume = avg?.monthlyRefuelsVolume ?? 0;
  const monthlyConsumption = avg?.monthlyConsumption ?? null;

  return {
    costs: [{ currency, totalCost: monthlyExpense }],
    col2Value: monthlyVolume > 0 ? monthlyVolume.toFixed(1) : "0",
    col2UnitLabel: getVolumeUnitLabel(volumeUnit, t),
    col3Value:
      monthlyConsumption != null && monthlyConsumption > 0
        ? monthlyConsumption.toFixed(1)
        : "—",
    col3UnitLabel:
      monthlyConsumption != null && monthlyConsumption > 0
        ? getConsumptionUnitLabel(consumptionUnit, t)
        : "",
  };
}

function getTcoDisplayValues(
  tco: CostOfOwnership | undefined,
  fallbackCurrency: string,
  distanceUnit: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): PeriodDisplayValues {
  if (!tco) {
    return {
      costs: [{ currency: fallbackCurrency, totalCost: 0 }],
      col2Value: "—",
      col2UnitLabel: "",
      col3Value: "—",
      col3UnitLabel: "",
    };
  }

  const totalCost = tco.totalCost ?? 0;
  const perMonth = tco.perMonth ?? null;
  const perDistance = tco.perDistance ?? null;
  const distUnitLabel = getDistanceUnitLabel(distanceUnit, t);

  return {
    costs: [{ currency: fallbackCurrency, totalCost }],
    col2Value: perMonth != null ? formatAmount(perMonth) : "—",
    col2UnitLabel: perMonth != null ? t("common:stats.perMonth", { defaultValue: "/mo" }) : "",
    col3Value: perDistance != null ? perDistance.toFixed(2) : "—",
    col3UnitLabel: perDistance != null ? `/${distUnitLabel}` : "",
  };
}

function usePeriodDisplayValues(
  period: StatsPeriod,
  item: DashboardItem,
  fallbackCurrency: string,
  distanceUnit: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): PeriodDisplayValues {
  return useMemo(() => {
    if (period === "tco") {
      return getTcoDisplayValues(item.costOfOwnership, fallbackCurrency, distanceUnit, t);
    }

    if (period === "average") {
      return getAverageDisplayValues(item, fallbackCurrency, t);
    }

    const stats =
      period === "current" ? item.currentMonthStats : item.previousMonthStats;
    const monetary =
      period === "current"
        ? item.currentMonthMonetary
        : item.previousMonthMonetary;

    return getMonthDisplayValues(stats, monetary, fallbackCurrency, t);
  }, [period, item, fallbackCurrency, distanceUnit, t]);
}

// ============================================
// Sub-Components
// ============================================

interface GradientPeriodSelectorProps {
  value: StatsPeriod;
  onChange: (value: StatsPeriod) => void;
  currentMonthStats: CarStats;
  previousMonthStats: CarStats;
  averages: DashboardAverages;
}

const GradientPeriodSelector: React.FC<GradientPeriodSelectorProps> = ({
  value,
  onChange,
  currentMonthStats,
  previousMonthStats,
  averages,
}) => {
  const { t } = useTranslation(["common"]);

  const currentLabel = useMemo(() => {
    if (currentMonthStats?.month > 0) {
      return t(`common:monthsShort.${currentMonthStats.month}`);
    }
    return t("common:stats.current");
  }, [currentMonthStats, t]);

  const previousLabel = useMemo(() => {
    if (previousMonthStats?.month > 0) {
      return t(`common:monthsShort.${previousMonthStats.month}`);
    }
    return t("common:stats.previous");
  }, [previousMonthStats, t]);

  const avgLabel = useMemo(() => {
    const months = averages?.monthsCount || 0;
    return t("common:stats.avgShort", { count: months });
  }, [averages, t]);

  const data = useMemo(
    () => [
      { label: currentLabel, value: "current" as StatsPeriod },
      { label: previousLabel, value: "previous" as StatsPeriod },
      { label: avgLabel, value: "average" as StatsPeriod },
      { label: t("common:stats.tco", { defaultValue: "TCO" }), value: "tco" as StatsPeriod },
    ],
    [currentLabel, previousLabel, avgLabel, t]
  );

  return (
    <SegmentedControl
      value={value}
      onChange={(val) => onChange(val as StatsPeriod)}
      data={data}
      size="xs"
      fullWidth
      styles={{
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          border: "none",
        },
        label: {
          color: "rgba(255, 255, 255, 0.8)",
          fontWeight: 500,
          fontSize: "var(--mantine-font-size-xs)",
        },
        indicator: {
          backgroundColor: "rgba(255, 255, 255, 0.25)",
          boxShadow: "none",
        },
        control: {
          border: "none",
        },
      }}
    />
  );
};

interface GradientStatColumnProps {
  label: string;
  value: string;
  unit?: string;
}

const GradientStatColumn: React.FC<GradientStatColumnProps> = ({
  label,
  value,
  unit,
}) => {
  const fontSize = getAdaptiveStatFontSize(value);

  return (
    <Stack gap={0} align="center" style={{ flex: 1, minWidth: 0 }}>
      <Text
        size="xs"
        tt="uppercase"
        fw={600}
        style={{ color: "rgba(255, 255, 255, 0.7)", letterSpacing: "0.05em" }}
      >
        {label}
      </Text>
      <Text
        fw={700}
        ff="var(--ff-mono)"
        ta="center"
        style={{
          color: "white",
          fontSize,
          letterSpacing: "-0.02em",
          lineHeight: 1.2,
        }}
      >
        {value}
      </Text>
      {unit && (
        <Text
          size="xs"
          fw={500}
          ta="center"
          style={{ color: "rgba(255, 255, 255, 0.6)", lineHeight: 1.3 }}
        >
          {unit}
        </Text>
      )}
    </Stack>
  );
};

interface GradientExpensesDisplayProps {
  costs: CurrencyCost[];
  fallbackCurrency: string;
  label: string;
}

const GradientExpensesDisplay: React.FC<GradientExpensesDisplayProps> = ({
  costs,
  fallbackCurrency,
  label,
}) => {
  const primaryCost = costs.length > 0 ? costs[0] : null;
  const primaryCurrency = primaryCost?.currency || fallbackCurrency;
  const primaryAmount = primaryCost?.totalCost ?? 0;
  const formattedAmount = formatAmount(primaryAmount);
  const fontSize = getAdaptiveStatFontSize(formattedAmount);

  const secondaryCosts = costs.length > 1 ? costs.slice(1) : [];

  return (
    <Stack gap={0} align="center" style={{ flex: 1, minWidth: 0 }}>
      <Text
        size="xs"
        tt="uppercase"
        fw={600}
        style={{ color: "rgba(255, 255, 255, 0.7)", letterSpacing: "0.05em" }}
      >
        {label}
      </Text>
      <Text
        fw={700}
        ff="var(--ff-mono)"
        ta="center"
        style={{
          color: "white",
          fontSize,
          letterSpacing: "-0.02em",
          lineHeight: 1.2,
        }}
      >
        {formattedAmount}
      </Text>
      <Text
        size="xs"
        fw={500}
        ta="center"
        style={{ color: "rgba(255, 255, 255, 0.6)", lineHeight: 1.3 }}
      >
        {primaryCurrency}
      </Text>

      {secondaryCosts.map((c) => (
        <React.Fragment key={c.currency}>
          <Text
            size="xs"
            fw={600}
            ff="var(--ff-mono)"
            ta="center"
            mt={4}
            style={{ color: "rgba(255, 255, 255, 0.7)", lineHeight: 1.2 }}
          >
            {formatAmount(c.totalCost)}
          </Text>
          <Text
            size="xs"
            fw={500}
            ta="center"
            style={{ color: "rgba(255, 255, 255, 0.5)", lineHeight: 1.3 }}
          >
            {c.currency}
          </Text>
        </React.Fragment>
      ))}
    </Stack>
  );
};

// ============================================
// Main Component
// ============================================

export const MobileVehicleCard: React.FC<MobileVehicleCardProps> = ({
  item,
  carId,
  currency = "USD",
  //vehicleType = "car",
  onQuickAction,
  showScanReceipt = false,
  onScanComplete,
  onDetectLocation,
  className,
}) => {
  const theme = useMantineTheme();
  const { t } = useTranslation(["common"]);

  const [period, setPeriod] = useState<StatsPeriod>("current");

  // ============================================
  // Odometer from total stats
  // ============================================

  const totalUnits: StatsUnits | undefined =
    item.totalStats?.inUserUnits || item.totalStats?.inCarUnits;
  const distanceUnitRaw = totalUnits?.distanceUnit || "km";
  const distanceUnitLabel = getDistanceUnitLabel(distanceUnitRaw, t);

  const odometer = totalUnits?.lastOdometer ?? 0;
  const formattedOdometer = Math.round(odometer).toLocaleString("en-US");
  const odometerFontSize = getAdaptiveOdometerFontSize(formattedOdometer);

  // Monthly change from current month stats
  const currentMonthUnits: StatsUnits | undefined =
    item.currentMonthStats?.inUserUnits || item.currentMonthStats?.inCarUnits;

  const monthlyChange = useMemo(() => {
    if (!currentMonthUnits) return null;
    const start = currentMonthUnits.firstOdometer;
    const end = currentMonthUnits.lastOdometer;
    if (start != null && end != null && end > start) {
      return end - start;
    }
    return null;
  }, [currentMonthUnits]);

  const formattedChange =
    monthlyChange != null && monthlyChange > 0
      ? `+${Math.round(monthlyChange).toLocaleString("en-US")}`
      : null;

  // ============================================
  // Period display values
  // ============================================

  const displayValues = usePeriodDisplayValues(
    period,
    item,
    currency,
    distanceUnitRaw,
    t
  );

  // ============================================
  // Column labels — adapt per period
  // ============================================

  const col1Label = useMemo(() => {
    if (period === "tco") return t("common:stats.total", { defaultValue: "TOTAL" });
    if (period === "average") return t("common:stats.avgSpent", { defaultValue: "AVG SPENT" });
    return t("common:stats.spent", { defaultValue: "SPENT" });
  }, [period, t]);

  const col2Label = useMemo(() => {
    if (period === "tco") return t("common:stats.perMonth", { defaultValue: "PER MO" });
    if (period === "average") return t("common:stats.avgFuel", { defaultValue: "AVG FUEL" });
    return t("common:stats.fuel", { defaultValue: "FUEL" });
  }, [period, t]);

  const col3Label = useMemo(() => {
    if (period === "tco") return t("common:stats.perDist", { defaultValue: "PER DIST" });
    return t("common:stats.economy", { defaultValue: "ECONOMY" });
  }, [period, t]);

  // ============================================
  // Render
  // ============================================

  return (
    <Stack gap="sm" className={className}>
      {/* Gradient Stats Card */}
      <Box
        p="md"
        style={{
          background: `linear-gradient(135deg, ${theme.colors.brand[7]} 0%, ${theme.colors.brand[5]} 100%)`,
          borderRadius: `var(--mantine-radius-lg)`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circle */}
        <Box
          style={{
            position: "absolute",
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: "50%",
            backgroundColor: "rgba(255, 255, 255, 0.06)",
            pointerEvents: "none",
          }}
        />

        <Stack gap="sm">
          {/* Odometer Section */}
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={0}>
              <Text
                size="xs"
                tt="uppercase"
                fw={600}
                style={{
                  color: "rgba(255, 255, 255, 0.7)",
                  letterSpacing: "0.05em",
                }}
              >
                {t("common:stats.odometer", { defaultValue: "ODOMETER" })}
              </Text>
              <Group gap={6} wrap="nowrap" align="baseline">
                <Text
                  fw={700}
                  ff="var(--ff-mono)"
                  style={{
                    color: "white",
                    fontSize: odometerFontSize,
                    letterSpacing: "-0.02em",
                    lineHeight: 1,
                  }}
                >
                  {formattedOdometer}
                </Text>
                <Text
                  size="md"
                  fw={500}
                  style={{ color: "rgba(255, 255, 255, 0.8)" }}
                >
                  {distanceUnitLabel}
                </Text>
              </Group>
            </Stack>

            {/* Monthly change — hide on TCO tab, irrelevant there */}
            {formattedChange && period !== "tco" && (
              <Group
                gap={4}
                wrap="nowrap"
                mt="sm"
                style={{ color: "rgba(255, 255, 255, 0.85)" }}
              >
                <AppIcon iconName="TrendingUp" size={14} stroke={2} />
                <Text size="sm" fw={600} ff="var(--ff-mono)">
                  {formattedChange}
                </Text>
              </Group>
            )}

            {/* TCO tab: show months owned instead of monthly change */}
            {period === "tco" && item.costOfOwnership?.monthsOwned != null && (
              <Group
                gap={4}
                wrap="nowrap"
                mt="sm"
                style={{ color: "rgba(255, 255, 255, 0.85)" }}
              >
                <AppIcon iconName="Calendar" size={14} stroke={2} />
                <Text size="sm" fw={600} ff="var(--ff-mono)">
                  {t("common:stats.monthsOwned", {
                    count: item.costOfOwnership.monthsOwned,
                    defaultValue: "{{count}} mo",
                  })}
                </Text>
              </Group>
            )}
          </Group>

          {/* Period Selector */}
          <GradientPeriodSelector
            value={period}
            onChange={setPeriod}
            currentMonthStats={item.currentMonthStats}
            previousMonthStats={item.previousMonthStats}
            averages={item.averages}
          />

          {/* Stats Row */}
          <Group gap={0} wrap="nowrap" justify="space-between">
            <GradientExpensesDisplay
              costs={displayValues.costs}
              fallbackCurrency={currency}
              label={col1Label}
            />

            <Box
              style={{
                width: 1,
                alignSelf: "stretch",
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                flexShrink: 0,
              }}
            />

            <GradientStatColumn
              label={col2Label}
              value={displayValues.col2Value}
              unit={displayValues.col2UnitLabel}
            />

            <Box
              style={{
                width: 1,
                alignSelf: "stretch",
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                flexShrink: 0,
              }}
            />

            <GradientStatColumn
              label={col3Label}
              value={displayValues.col3Value}
              unit={displayValues.col3UnitLabel}
            />
          </Group>
        </Stack>
      </Box>

      {/* Quick Actions */}
      {carId && (
        <VehicleCardQuickActions
          carId={carId}
          size="mobile"
          onAction={onQuickAction}
          showScanReceipt={showScanReceipt}
          onScanComplete={onScanComplete}
          onDetectLocation={onDetectLocation}
        />
      )}
    </Stack>
  );
};